import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, MapPin, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import useApiData from "@/hooks/useApiData";
import FilterBar from "@/components/FilterBar";
import ListState from "@/components/ListState";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "junior", label: "Junior" },
  { id: "adult", label: "Adult" },
  { id: "lessons", label: "Lessons" },
];

const CATEGORY_LABELS = { leagues_camps: "Camps & Leagues", junior: "Academy" };
const LOCATION_LABELS = { both: "Both Locations", "baltimore-main": "DS Timonium", "baltimore-second": "Coppermine", baltimore: "Baltimore", princeton: "Princeton" };

const ProgramCard = ({ program }) => (
  <div className="program-card" data-testid={`program-${program.id}`}>
    <span className="font-mono text-[10px] text-[#0D6B56] tracking-wider uppercase">
      {CATEGORY_LABELS[program.category] || program.category}
    </span>
    <h3 className="font-display text-lg font-bold text-[#F2EDE4] mt-2 mb-2">{program.name}</h3>
    <p className="text-sm text-[#F2EDE4]/30 mb-4 line-clamp-2">{program.description}</p>
    <div className="space-y-2 mb-4">
      <div className="flex items-start gap-2 text-sm text-[#F2EDE4]/30">
        <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#F2EDE4]/20" />
        <span>{program.schedule}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-[#F2EDE4]/30">
        <MapPin className="w-3.5 h-3.5 flex-shrink-0 text-[#F2EDE4]/20" />
        <span>{LOCATION_LABELS[program.location] || program.location}</span>
      </div>
    </div>
    <p className="text-sm font-semibold text-[#0D6B56] mb-4">{program.price}</p>
    {program.features?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-4">
        {program.features.slice(0, 3).map((f) => (
          <span key={f} className="text-[10px] text-[#F2EDE4]/20 bg-white/5 px-2 py-1">{f}</span>
        ))}
      </div>
    )}
    <Link to="/contact" className="text-[11px] font-semibold text-[#F2EDE4]/40 hover:text-[#0D6B56] transition-colors inline-flex items-center gap-1 uppercase tracking-wider" data-testid={`program-register-${program.id}`}>
      Register <ArrowRight className="w-3 h-3" />
    </Link>
  </div>
);

const Programs = () => {
  const { category } = useParams();
  const [activeCategory, setActiveCategory] = useState(category || "all");
  const { data: programs, loading } = useApiData(api.getPrograms, activeCategory === "all" ? {} : { category: activeCategory });

  useEffect(() => {
    if (category) setActiveCategory(category);
  }, [category]);

  return (
    <div className="pt-16" data-testid="programs-page" style={{ background: "var(--lrg-bg)" }}>
      <section className="py-16" style={{ background: "var(--lrg-surface)" }}>
        <div className="container-custom">
          <div className="overline mb-4">Training Programs</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-[#F2EDE4] mb-3" data-testid="programs-title">Programs</h1>
          <p className="text-[#F2EDE4]/40 max-w-md">Structured training for every level.</p>
        </div>
      </section>

      <FilterBar options={CATEGORIES} active={activeCategory} onChange={setActiveCategory} />

      <section className="section" data-testid="programs-grid">
        <div className="container-custom">
          <ListState loading={loading} isEmpty={programs.length === 0} emptyText="No programs found.">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {programs.map((program) => <ProgramCard key={program.id} program={program} />)}
            </div>
          </ListState>
        </div>
      </section>
    </div>
  );
};

export default Programs;
